import { isEmpty, isString } from './assertion.utils';

/**
 * @description
 * find a sub-string inside a main notation-formatted string. for example:
 * input: "employee.address.residence" output: last notation: "residence"
 * @param value an annotated string
 * @param delimiter the annotation format delimiter
 * @returns the last expression found in the notated main string
 */
export function findLastNotation(value: string, delimiter: string = '.'): string {
  const lastPos = value.lastIndexOf(delimiter);
  if (lastPos < 0) {
    return value;
  }
  return value.slice((value.length - 1 - lastPos) * -1);
}

/**
 * @description
 * remove spaces at the end and start of the string
 * */
export function trim(value: string): string {
  return value.replace(/\s/g, '');
}

/**
 * @description
 * remove multiple consecutive spaces off the entire string. single spaces remain untouched.
 * example: " hel  lo w    w or ld" => "hello world"
 */
export function trimMulti(value: string): string {
  if (!isString(value)) {
    return value;
  }
  return value.replace(/\s+/g, ' ');
}

/**
 * @description
 * remove multiple consecutive spaces off the entire string. single spaces also will be trimmed.
 * example: " hel  lo w    w or ld" => "helloworld"
 */
export function trimComplete(value: string) {
  if (!isString(value)) {
    return value;
  }
  return value.replace(/\s+/g, '');
}

/**
 * @description
 * reversing the order of the words in a given string starting at the position of a character that being provided to the fn.
 * useful when trim() does not solving issues of merging hebrew with english strings
 * examples: " hello world" => "worldhello" | "50 ₪"  => "₪50" 
 * @param value the original string
 * @param origChar the character that determines where to replace the order of the string words
 * @param newChar an optional param that can replace the reversing character - if not provider will concat the 2 strings between the origChar (see examples)
 * @returns new string with reversed words order
 */
export function reverseWordsOrder(value: string, origChar: string, newChar = '') {
  if (!isString(value)) {
    return value;
  }
  return value.split(origChar).reverse().join(newChar);
}

/**
 * @description
 * replace N chars of a given string value with a blank char ('*')
 * @param inputString the target string to partially replace its chars
 * @param startPosition the index of the first char to replace
 * @param count how many chars to replace, when negative value - count starts at the end of the string
 * @param maskCharacter optional param to determine the char that will mask the inputString - default set to asterisks characters mask ( ****** )
 * @returns the original string with chars replaced
 * @example:
 *  hideChars(originalString, 0, 5) => "*****o, World!"
 *  hideChars(originalString, 7, 5) => "Hello, *****!"
 *  hideChars(originalString, -5, 5) => "Hello, W*****!";
 */
export function maskChars(inputString: string, startPosition: number = 0, count: number = inputString.length, maskCharacter: string = '*'): string {
  if (startPosition < 0) {
    startPosition += inputString.length;
  }

  if (count < 0) {
    count = inputString.length - startPosition + count;
  }

  if (count <= 0 || startPosition < 0 || startPosition >= inputString.length) {
    return inputString; // Nothing to hide or invalid arguments
  }

  const totalChars = inputString.length;
  const endPosition = startPosition + count;

  if (endPosition >= totalChars) {
    return inputString.substring(0, startPosition) + maskCharacter.repeat(totalChars - startPosition);
  }

  const visibleCharsBefore = startPosition;
  const maskSigns = maskCharacter.repeat(count);

  return inputString.substring(0, visibleCharsBefore) + maskSigns + inputString.substring(endPosition, totalChars);
}

export function compareStrings(source: string, target: string): boolean {
  if (isEmpty(source) || isEmpty(target) ) {
    return false;
  }
  
  return source.toLowerCase().includes(target.toLowerCase());
}